import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { useAdminAuditLog } from '@/hooks/useAdmin';
import { useAdminUsers } from '@/hooks/useAdmin';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Pagination } from '@/components/ui/Pagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatDate } from '@/utils/format';

const actionVariant: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  create: 'success', update: 'info', delete: 'error', login: 'default', logout: 'default',
};

export function AdminAuditLogPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data: usersData } = useAdminUsers({ perPage: 100 });
  const userOptions = [{ value: '', label: t('common.all') }, ...(usersData?.items ?? []).map((u: { id: string; firstName: string; lastName: string }) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))];
  const { data, isLoading, isError, refetch } = useAdminAuditLog({ page, perPage, action: actionFilter, userId: userFilter || undefined, from: from || undefined, to: to || undefined });

  if (isError) return <ErrorState onRetry={() => refetch()} />;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.auditLog')}</h1>

      <Card>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <Select options={userOptions} value={userFilter} onChange={setUserFilter} />
          <Select options={[{ value: '', label: t('common.all') }, { value: 'create', label: t('common.create') }, { value: 'update', label: t('common.update') }, { value: 'delete', label: t('common.delete') }, { value: 'login', label: t('auth.login') }, { value: 'logout', label: t('auth.logout') }]} value={actionFilter} onChange={setActionFilter} />
          <Input label={t('common.from')} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <Input label={t('common.to')} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>

        {isLoading ? (
          <div className="space-y-3">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}</div>
        ) : data?.items?.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-4 py-2">{t('admin.time')}</th>
                    <th className="px-4 py-2">{t('admin.user')}</th>
                    <th className="px-4 py-2">{t('admin.action')}</th>
                    <th className="px-4 py-2">{t('common.details')}</th>
                    <th className="px-4 py-2">{t('admin.ipAddress')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((log: { id: string; createdAt: string; userName: string; action: string; resourceType?: string; details?: Record<string, unknown>; ipAddress: string }) => (
                    <tr key={log.id} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="whitespace-nowrap px-4 py-3 text-gray-500 dark:text-gray-400">{formatDate(log.createdAt, 'dd/MM/yyyy HH:mm')}</td>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{log.userName}</td>
                      <td className="px-4 py-3"><Badge label={String(t(`auditLog.${log.action}`, { defaultValue: log.action }))} variant={actionVariant[log.action] ?? 'default'} /></td>
                      <td className="max-w-xs truncate px-4 py-3 text-gray-600 dark:text-gray-300">{log.resourceType}{log.details ? `: ${typeof log.details === 'object' ? Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ') : String(log.details)}` : ''}</td>
                      <td className="px-4 py-3 text-xs text-gray-400">{log.ipAddress}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.totalPages > 1 && <div className="mt-4 px-4"><Pagination page={data.page} perPage={data.perPage} total={data.total} totalPages={data.totalPages} onPageChange={setPage} onPerPageChange={(pp) => { setPerPage(pp); setPage(1); }} /></div>}
          </>
        ) : (
          <EmptyState icon={ClipboardDocumentListIcon} title={t('admin.noAuditLogs')} />
        )}
      </Card>
    </div>
  );
}
